import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import { StatTile, PeriodSelect, PERIOD_OPTIONS } from './StatTile.tsx'

interface LowMarginItem {
  itemCode: string
  itemDescription: string | null
  lowMarginCount: number
  avgMarginPct: number
  minMarginPct: number
  totalRevenue: number
  totalGrossProfit: number
  lastSoldDate: string | null
}

function fmtEur(v: number) {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v)
}

function MarginBadge({ pct }: { pct: number }) {
  if (pct < 0) return (
    <span className="inline-block px-1.5 py-0.5 rounded text-caption font-medium bg-red-100 text-red-700">
      {pct.toFixed(1)} %
    </span>
  )
  return (
    <span className="inline-block px-1.5 py-0.5 rounded text-caption font-medium bg-amber-100 text-amber-700">
      {pct.toFixed(1)} %
    </span>
  )
}

interface Props {
  tenantId?: string | null
}

export function SchwachmargenAlarmTile({ tenantId }: Props) {
  const [days, setDays] = useState(90)
  const [threshold, setThreshold] = useState(10)

  const { data, isLoading, error } = useQuery<{ items: LowMarginItem[]; threshold: number }>({
    queryKey: ['stats.low-margin', days, threshold, tenantId],
    queryFn: () => {
      const params = new URLSearchParams({
        days: String(days),
        threshold: String(threshold),
        minCount: '1',
        limit: '15',
      })
      if (tenantId) params.set('tenantId', tenantId)
      return apiClient.get(`/stats/low-margin-articles?${params}`).then((r) => r.data)
    },
    staleTime: 5 * 60 * 1000,
  })

  const items = data?.items ?? []

  return (
    <StatTile
      title="Schwachmargen-Alarm"
      subtitle="Artikel wiederholt unter Marge-Schwellwert verkauft"
      loading={isLoading}
      error={error as Error | null}
      empty={!isLoading && !error && items.length === 0}
      emptyTitle="Kein Alarm"
      emptyMessage={`Keine Artikel unter ${threshold} % Marge im gewählten Zeitraum.`}
      headerRight={
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            <span className="text-caption text-gray-500">Ziel</span>
            <input
              type="number"
              value={threshold}
              min={0}
              max={100}
              step={1}
              onChange={(e) => setThreshold(Math.max(0, Math.min(100, Number(e.target.value))))}
              className="w-12 text-caption border border-gray-200 rounded px-1.5 py-1 text-center focus:outline-none focus:ring-1 focus:ring-brand-400"
            />
            <span className="text-caption text-gray-500">%</span>
          </div>
          <PeriodSelect value={days} options={PERIOD_OPTIONS} onChange={setDays} />
        </div>
      }
    >
      <div className="space-y-2.5">
        {items.map((item) => (
          <div key={item.itemCode} className="flex items-start justify-between gap-3 py-1.5 border-b border-gray-50 last:border-0">
            <div className="flex items-start gap-2 min-w-0">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-500 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <div className="text-small font-medium text-gray-900 truncate">
                  {item.itemDescription ?? item.itemCode}
                </div>
                <div className="text-caption text-gray-400">
                  {item.itemCode} · {item.lowMarginCount}× verkauft · {fmtEur(item.totalRevenue)} Umsatz
                </div>
              </div>
            </div>
            <div className="shrink-0 text-right">
              <MarginBadge pct={item.avgMarginPct} />
              <div className="text-caption text-gray-400 mt-0.5">Ø Marge</div>
            </div>
          </div>
        ))}
      </div>
    </StatTile>
  )
}
